import React from 'react'
import axios from 'axios'
import LayoutStore from '../State/LayoutStore'
import LoginModal from '../Pages/LoginPage/LoginModal'
import SessionStore from '../State/SessionStore'
import * as Sentry from '@sentry/react'
import decircular from 'decircular'
import { redirect } from '@tanstack/react-router'
import { usingNewLogin } from '../State/Permissions/HasPermissions'
import { clerk } from '../App'
import posthog from 'posthog-js'

export async function makeRequest({
    path,
    method,
    data,
    baseURL = process.env.REACT_APP_SERVER_URL,
    maxRetries = 1,
}) {
    //const maxRetries = 3
    const retryDelay = 500 // Delay in milliseconds
    let attempts = 0
    data = decircular(data)

    // Check if this is a login request - if so, we handle errors differently
    const isLoginRequest = path.includes('/api/v1.5/user/login')

    while (attempts < maxRetries) {
        try {
            const headers = {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json',
            }
            if (usingNewLogin()) {
                const token = await clerk?.session?.getToken?.({
                    template: 'coincraft',
                })
                headers.Authorization = `Bearer ${token}`
            }
            if (SessionStore.userId) headers['x-user-id'] = SessionStore.userId
            if (SessionStore.organisationId)
                headers['x-org-id'] = SessionStore.organisationId

            // Add PostHog session info for server-side event tracking
            try {
                const sessionId = posthog.get_session_id()
                const distinctId = posthog.get_distinct_id()
                if (sessionId) headers['x-posthog-session-id'] = sessionId
                if (distinctId) headers['x-posthog-distinct-id'] = distinctId
            } catch (error) {
                // PostHog might not be initialized yet, that's ok
                console.debug(
                    'PostHog session info not available:',
                    error.message
                )
            }

            const response = await axios.request({
                baseURL: baseURL,
                url: path,
                method,
                data,
                timeout: 30 * 1000,
                withCredentials: true,
                headers,
            })
            return response
        } catch (error) {
            // Check if the error is a timeout
            if (
                axios.isCancel(error) ||
                error.code === 'ECONNABORTED' ||
                error.message.includes('timeout')
            ) {
                attempts = maxRetries
                throw error // Do not retry on timeouts
            }

            // Special case for login requests - don't redirect to login page for login failures
            if (error.response?.status === 401 && !isLoginRequest) {
                await SessionStore.checkAuthentication()
                attempts = maxRetries
                if (!usingNewLogin() && LayoutStore.pageRendered) {
                    LayoutStore.openModal(
                        <LoginModal modalId={'login-modal'} />
                    )
                } else {
                    window.location.replace('/login')
                }
            }
            attempts++
            // check login
            if (attempts >= maxRetries) {
                Sentry.captureException(error)
                posthog.captureException(error)
                // Handle error or throw it after max retries
                if (error.response) {
                    console.error(error.response)
                } else if (error.request) {
                    console.error(error.request)
                } else {
                    console.error('Error', error.message)
                }
                throw error
            }

            // Wait for a specified time before retrying
            await new Promise((resolve) => setTimeout(resolve, retryDelay))
        }
    }
}
